import { useState, useRef, useEffect, useCallback } from 'react';
import './BackgroundRemover.css';

const BackgroundRemover = () => {
  // State for Bulk Processing
  const [imageList, setImageList] = useState([]); // [{ id, original, processed, mask, status, progress }]
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [error, setError] = useState(null);

  // Editor States
  const [brushMode, setBrushMode] = useState('erase'); // 'erase', 'restore'
  const [brushSize, setBrushSize] = useState(30);
  const [isDrawing, setIsDrawing] = useState(false);

  // Refs
  const segmenterRef = useRef(null);
  const mainCanvasRef = useRef(null);
  const maskCanvasRef = useRef(null); // High-res offscreen mask
  const displayCanvasRef = useRef(null); // The one the user sees

  // Load AI Engine (MediaPipe)
  useEffect(() => {
    const loadMediaPipe = async () => {
      try {
        setIsModelLoading(true);
        if (!window.SelfieSegmentation) {
          await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation');
        }
        
        const { SelfieSegmentation } = window;
        const selfieSegmentation = new SelfieSegmentation({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`,
        });

        selfieSegmentation.setOptions({ modelSelection: 1 });
        selfieSegmentation.onResults(handleAIResults);
        segmenterRef.current = selfieSegmentation;
        
        setIsModelLoading(false);
      } catch (err) {
        console.error('AI Engine Error:', err);
        setError('Failed to start AI Engine.');
        setIsModelLoading(false);
      }
    };
    loadMediaPipe();
  }, []);

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  // --- Image Handling ---

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files).slice(0, 5); // Limit to 5
    if (files.length === 0) return;

    const newImages = files.map((file, i) => ({
      id: Date.now() + i,
      name: file.name,
      original: URL.createObjectURL(file),
      processed: null,
      mask: null,
      status: 'queued',
      progress: 0
    }));

    setImageList(prev => [...prev, ...newImages]);
    setActiveIndex(imageList.length > 0 ? activeIndex : 0);
  };

  const removeImage = (id) => {
    setImageList(prev => prev.filter(img => img.id !== id));
    if (activeIndex >= imageList.length - 1) setActiveIndex(Math.max(0, activeIndex - 1));
  };

  // --- AI Processing ---

  const resolveRef = useRef(null);
  
  const handleAIResults = (results) => {
    if (!resolveRef.current) return;
    
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = results.image.width;
    maskCanvas.height = results.image.height;
    const mctx = maskCanvas.getContext('2d');
    mctx.drawImage(results.segmentationMask, 0, 0);

    const resolver = resolveRef.current;
    resolveRef.current = null; // Clear to prevent double resolution
    resolver({
      mask: maskCanvas,
      width: results.image.width,
      height: results.image.height
    });
  };

  const processImage = async (index) => {
    if (index < 0 || !segmenterRef.current) return;
    
    let isAlreadyDone = false;
    setImageList(prev => {
        if (prev[index]?.status === 'done' || prev[index]?.status === 'processing') {
            isAlreadyDone = true;
        }
        return prev;
    });
    if (isAlreadyDone) return;

    setImageList(prev => {
      const copy = [...prev];
      if (copy[index]) copy[index].status = 'processing';
      return copy;
    });

    try {
      const target = imageList[index];
      if (!target) return;

      const img = new Image();
      img.src = target.original;
      await img.decode();

      // Use a timeout to prevent hanging forever
      const aiOutput = await Promise.race([
        new Promise((resolve) => {
          resolveRef.current = resolve;
          segmenterRef.current.send({ image: img });
        }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('AI Timed Out')), 15000))
      ]);

      // --- Saliency Recovery Pass ---
      // Intelligently expand the mask to capture missing parts of the object
      const refinedMask = await refineMaskWithSaliency(img, aiOutput.mask);

      // Apply initial mask to get processed image
      const finalUrl = generateProcessedUrl(img, refinedMask);

      setImageList(prev => {
        const copy = [...prev];
        copy[index].status = 'done';
        copy[index].processed = finalUrl;
        copy[index].mask = refinedMask; // Keep refined mask for manual editing
        return copy;
      });

    } catch (err) {
      console.error('Processing error:', err);
      setImageList(prev => {
        const copy = [...prev];
        copy[index].status = 'error';
        return copy;
      });
    }
  };

  const generateProcessedUrl = (originalImg, maskCanvas) => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = originalImg.width;
    tempCanvas.height = originalImg.height;
    const tctx = tempCanvas.getContext('2d');
    
    tctx.drawImage(maskCanvas, 0, 0);
    tctx.globalCompositeOperation = 'source-in';
    tctx.drawImage(originalImg, 0, 0);
    
    return tempCanvas.toDataURL('image/png');
  };

  const refineMaskWithSaliency = async (originalImg, aiMaskCanvas) => {
    return new Promise((resolve) => {
      const width = originalImg.width;
      const height = originalImg.height;
      
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(aiMaskCanvas, 0, 0);
      
      const maskData = ctx.getImageData(0, 0, width, height);
      const mData = maskData.data;
      
      const imgCanvas = document.createElement('canvas');
      imgCanvas.width = width;
      imgCanvas.height = height;
      const ictx = imgCanvas.getContext('2d');
      ictx.drawImage(originalImg, 0, 0);
      const iData = ictx.getImageData(0, 0, width, height).data;

      // Iterative Subject Recovery (Region Growing)
      // Capture parts that match the color profile of the detected subject
      for (let iter = 0; iter < 3; iter++) { // 3 passes for growth
        for (let i = 0; i < mData.length; i += 4) {
          if (mData[i+3] < 50) { // If currently transparent
            // Check neighbors (left/right/top/bottom)
            const neighbors = [i-4, i+4, i-(width*4), i+(width*4)];
            for (const n of neighbors) {
              if (n >= 0 && n < mData.length && mData[n+3] > 200) {
                // Check if color is similar to the successful subject neighbor
                const dR = Math.abs(iData[i] - iData[n]);
                const dG = Math.abs(iData[i+1] - iData[n+1]);
                const dB = Math.abs(iData[i+2] - iData[n+2]);
                
                if (dR + dG + dB < 45) { // Similarity threshold
                  mData[i+3] = 255; // Reclaim into mask
                  break;
                }
              }
            }
          }
        }
      }
      
      ctx.putImageData(maskData, 0, 0);
      resolve(canvas);
    });
  };

  // --- Manual Refine (Canvas Logic) ---

  const initEditor = useCallback(() => {
    const active = imageList[activeIndex];
    if (!active || !active.mask) return;

    const displayCanvas = displayCanvasRef.current;
    if (!displayCanvas) return;

    const ctx = displayCanvas.getContext('2d');
    const img = new Image();
    img.src = active.original;
    img.onload = () => {
      displayCanvas.width = img.width;
      displayCanvas.height = img.height;
      renderFrame();
    };
  }, [activeIndex, imageList]);

  useEffect(() => {
    if (activeIndex !== -1 && imageList[activeIndex]?.status === 'done') {
      initEditor();
    }
  }, [activeIndex, imageList, initEditor]);

  const renderFrame = () => {
    const active = imageList[activeIndex];
    const canvas = displayCanvasRef.current;
    if (!canvas || !active) return;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.src = active.original;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw only what's allowed by the mask
    ctx.save();
    ctx.drawImage(active.mask, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(img, 0, 0);
    ctx.restore();
  };

  const handlePointerDown = (e) => {
    setIsDrawing(true);
    handlePointerMove(e);
  };

  const handlePointerMove = (e) => {
    if (!isDrawing) return;
    const canvas = displayCanvasRef.current;
    const active = imageList[activeIndex];
    if (!canvas || !active || !active.mask) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const mctx = active.mask.getContext('2d');
    mctx.lineWidth = brushSize;
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';
    
    if (brushMode === 'erase') {
      mctx.globalCompositeOperation = 'destination-out';
    } else {
      mctx.globalCompositeOperation = 'source-over';
      // When restoring, we need a white brush on the mask
      mctx.fillStyle = 'white';
      mctx.strokeStyle = 'white';
    }

    mctx.beginPath();
    mctx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
    mctx.fill();
    
    renderFrame();
  };

  const saveRefinement = () => {
    const active = imageList[activeIndex];
    const img = new Image();
    img.src = active.original;
    img.onload = () => {
        const finalUrl = generateProcessedUrl(img, active.mask);
        setImageList(prev => {
            const copy = [...prev];
            copy[activeIndex].processed = finalUrl;
            return copy;
        });
    };
  };

  const [isEnhancing, setIsEnhancing] = useState(false);

  const enhanceImage = async () => {
      const active = imageList[activeIndex];
      if (!active || !active.processed || isEnhancing) return;

      setIsEnhancing(true);
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = active.processed;
        });

        canvas.width = img.width;
        canvas.height = img.height;
        
        // Professional Post-Processing Chain
        // High Contrast + Unsharp Mask Approximation + Vibrance
        ctx.filter = 'contrast(1.1) saturate(1.15) brightness(1.02) contrast(1.05) saturate(1.1)';
        ctx.drawImage(img, 0, 0);
        
        // Secondary pass for sharpness
        ctx.globalCompositeOperation = 'overlay';
        ctx.globalAlpha = 0.3;
        ctx.drawImage(img, 0, 0);
        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';

        const enhancedUrl = canvas.toDataURL('image/png', 1.0);
        
        setImageList(prev => {
            const copy = [...prev];
            copy[activeIndex].processed = enhancedUrl;
            return copy;
        });
        
        // Tiny delay for visual feedback
        await new Promise(r => setTimeout(r, 800));
      } catch (err) {
          console.error('Enhance failed:', err);
      } finally {
          setIsEnhancing(false);
      }
  };

  return (
    <div className="br-container">
      {imageList.length === 0 ? (
        <div className="br-upload-card">
           <div className="br-upload-inner">
            <div className="br-icon-sphere">📸</div>
            <h1>Bulk Background Remover</h1>
            <p className="br-tagline">Upload up to 5 images. AI + Manual Refinement.</p>
            <label className="br-upload-btn">
              <span>{isModelLoading ? 'System Loading...' : 'Select Images'}</span>
              <input type="file" multiple accept="image/*" onChange={handleFileUpload} hidden disabled={isModelLoading} />
            </label>
            <div className="br-features">
              <div className="br-feat-item">📦 Bulk Processing</div>
              <div className="br-feat-item">🖌️ Manual Brush</div>
              <div className="br-feat-item">✨ Auto Enhance</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="br-studio-layout">
          {/* Main Editor */}
          <div className="br-main-editor">
            <div className="br-editor-header">
              <div className="br-file-info">
                <span>{imageList[activeIndex]?.name}</span>
                <span className={`br-badge ${imageList[activeIndex]?.status}`}>
                  {imageList[activeIndex]?.status}
                </span>
              </div>
              <div className="br-editor-actions">
                <button className={`br-btn-sm ${isEnhancing ? 'pulse' : ''}`} onClick={enhanceImage} disabled={imageList[activeIndex]?.status !== 'done' || isEnhancing}>
                    {isEnhancing ? '✨ Enhancing...' : '✨ Enhance'}
                </button>
                <button className="br-btn-sm primary" onClick={() => {
                  const link = document.createElement('a');
                  link.download = `nexaai-${Date.now()}.png`;
                  link.href = imageList[activeIndex].processed;
                  link.click();
                }} disabled={!imageList[activeIndex]?.processed}>Download</button>
              </div>
            </div>

            <div className="br-canvas-viewport">
              {imageList[activeIndex]?.status === 'done' ? (
                <div className="br-canvas-container">
                    {isProcessing && <div className="br-scan-bar"></div>}
                    <canvas 
                    ref={displayCanvasRef} 
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={() => { setIsDrawing(false); saveRefinement(); }}
                    onPointerLeave={() => setIsDrawing(false)}
                    />
                </div>
              ) : (
                <div className="br-loading-overlay">
                  {imageList[activeIndex]?.status === 'queued' ? (
                    <button className="br-start-btn" onClick={() => processImage(activeIndex)}>Start AI Removal</button>
                  ) : (
                    <div className="br-spinner"></div>
                  )}
                </div>
              )}
            </div>

            {/* Toolbar */}
            <div className="br-toolbar">
              <div className="br-tool-group">
                <button className={`br-tool-btn ${brushMode === 'erase' ? 'active' : ''}`} onClick={() => setBrushMode('erase')}>🧼 Erase</button>
                <button className={`br-tool-btn ${brushMode === 'restore' ? 'active' : ''}`} onClick={() => setBrushMode('restore')}>🖍️ Restore</button>
              </div>
              <div className="br-size-slider">
                <label>Brush Size</label>
                <input type="range" min="5" max="100" value={brushSize} onChange={(e) => setBrushSize(parseInt(e.target.value))} />
              </div>
            </div>
          </div>

          {/* Sidebar / Gallery */}
          <div className="br-sidebar-gallery">
            <div className="br-sidebar-header">
              <h3>Bulk Queue ({imageList.length}/5)</h3>
              <label className="br-add-btn">
                + Add
                <input type="file" multiple accept="image/*" onChange={handleFileUpload} hidden />
              </label>
            </div>
            <div className="br-gallery-list">
              {imageList.map((img, i) => (
                <div key={img.id} className={`br-gallery-item ${i === activeIndex ? 'active' : ''}`} onClick={() => setActiveIndex(i)}>
                  <div className="br-thumb">
                    <img src={img.original} alt="" />
                    {img.status === 'processing' && <div className="br-mini-loader"></div>}
                  </div>
                  <div className="br-item-details">
                    <p>{img.name}</p>
                    <span className={img.status}>{img.status}</span>
                  </div>
                  <button className="br-delete-btn" onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}>×</button>
                </div>
              ))}
            </div>
            
            <div className="br-bulk-actions">
               <button 
                className="br-process-all" 
                onClick={async () => {
                  for (let i = 0; i < imageList.length; i++) {
                    // Process images one by one to avoid GPU/Memory deadlock
                    await processImage(i);
                  }
               }}>Process All Images</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackgroundRemover;